import { useState } from "react";
import { useParams, Link } from "react-router";
import Seo from "../components/Seo.tsx";
import { useProducts } from "../lib/products-context.tsx";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { Button, ButtonLink } from "../components/ui/Button.tsx";
import { createCheckoutSession, ApiError } from "../lib/api.ts";
import { useCart } from "../lib/cart.tsx";

const TYPE_LABEL: Record<string, string> = {
  jar: "9.5 oz Jar Candle",
  signature: "Signature Collection",
  sculptural: "Sculptural Candle",
  slate: "Aroma Slate",
  bouquet: "Floral Wax Bouquet",
  spray: "Room & Linen Spray",
};

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { products, live } = useProducts();
  const product = products.find((p) => p.slug === slug);
  const [quantity, setQuantityState] = useState(1);
  const { add } = useCart();
  const [checkoutState, setCheckoutState] = useState<
    | { status: "idle" }
    | { status: "pending" }
    | { status: "error"; message: string }
  >({ status: "idle" });

  async function handleBuyNow() {
    if (!product || product.priceCents == null) return;
    setCheckoutState({ status: "pending" });
    try {
      const { url } = await createCheckoutSession([
        { slug: product.slug, quantity },
      ]);
      window.location.href = url;
    } catch (err) {
      setCheckoutState({
        status: "error",
        message:
          err instanceof ApiError
            ? err.message
            : "Could not start checkout — please try again.",
      });
    }
  }

  function handleAddToCart() {
    if (!product || product.priceCents == null) return;
    add(product.slug, quantity);
  }

  if (!product) {
    // A bot-added product isn't in the build-time snapshot — don't show
    // "not found" until the live catalog has answered.
    if (!live) {
      return (
        <div className="mx-auto max-w-2xl px-6 py-32 text-center md:px-10">
          <span className="eyebrow">The Collection</span>
          <p className="mt-6 text-sm text-[var(--color-stone)]">
            Fetching this piece…
          </p>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center md:px-10">
        <span className="eyebrow">Not Found</span>
        <h1 className="mt-6 font-serif text-4xl text-[var(--color-emerald-deep)] md:text-5xl">
          This piece isn't in the collection.
        </h1>
        <p className="mt-6 text-sm text-[var(--color-stone)]">
          It may have been retired, or the link is mistaken.
        </p>
        <div className="mt-10">
          <ButtonLink to="/collection">Back to the Collection</ButtonLink>
        </div>
      </div>
    );
  }

  const { name, subtitle, scent, description, priceLabel, tags, image, type } =
    product;

  const related = products
    .filter((p) => p.slug !== product.slug)
    .slice(0, 3);

  return (
    <div className="mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20">
      <Seo
        title={`${name} — BeAdoré Candles`}
        description={description.slice(0, 155)}
        path={`/product/${slug}`}
        image={`https://www.beadorecandles.com${image}`}
        type="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name,
            description,
            image: `https://www.beadorecandles.com${image}`,
            brand: { "@type": "Brand", name: "BeAdoré" },
            offers: {
              "@type": "Offer",
              price: product.priceCents
                ? (product.priceCents / 100).toFixed(2)
                : undefined,
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              url: `https://www.beadorecandles.com/product/${slug}`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.beadorecandles.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "The Collection",
                item: "https://www.beadorecandles.com/collection",
              },
              {
                "@type": "ListItem",
                position: 3,
                name,
                item: `https://www.beadorecandles.com/product/${slug}`,
              },
            ],
          },
        ]}
      />
      <Link
        to="/collection"
        className="mb-10 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] text-[var(--color-stone)] transition-colors hover:text-[var(--color-emerald)]"
      >
        <span aria-hidden>←</span> The Collection
      </Link>

      <section className="grid gap-10 md:grid-cols-2 md:gap-16">
        <div
          className="relative aspect-[4/5] overflow-hidden border border-[var(--color-rule)]/60 bg-[var(--color-warm-white)]"
          style={{ animation: "var(--animate-fade-up)" }}
        >
          <img
            src={image}
            alt={name}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <Feather
            className="pointer-events-none absolute top-6 right-6 h-20 w-auto rotate-[18deg] opacity-50"
            style={{
              animation: "var(--animate-fade-in)",
              animationDelay: "400ms",
            }}
          />
        </div>

        <div
          className="flex flex-col"
          style={{
            animation: "var(--animate-fade-up)",
            animationDelay: "140ms",
          }}
        >
          <span className="eyebrow">{TYPE_LABEL[type] ?? "BeAdoré"}</span>
          <h1 className="mt-5 font-serif text-5xl leading-[1.02] text-[var(--color-emerald-deep)] md:text-6xl">
            {name}
          </h1>
          {subtitle ? (
            <p className="mt-3 font-serif text-xl italic text-[var(--color-amber)]">
              {subtitle}
            </p>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className={[
                  "rounded-full border px-3 py-1 text-[0.62rem] font-medium uppercase tracking-[0.14em]",
                  tag === "Bestseller"
                    ? "border-[var(--color-amber)] bg-[var(--color-amber)] text-[var(--color-warm-white)]"
                    : tag === "New"
                      ? "border-[var(--color-emerald)] bg-[var(--color-emerald-deep)] text-[var(--color-sage-light)]"
                      : tag === "Signature"
                        ? "border-[var(--color-gold)] text-[var(--color-gold-deep)]"
                        : tag === "Made to Order"
                          ? "border-[var(--color-oxblood)]/60 text-[var(--color-oxblood)]"
                          : "border-[var(--color-sage-light)] text-[var(--color-sage)]",
                ].join(" ")}
              >
                {tag}
              </span>
            ))}
          </div>

          <Divider className="my-9" />

          <div>
            <p className="text-[0.62rem] font-medium uppercase tracking-[0.24em] text-[var(--color-amber)]">
              Scent
            </p>
            <p className="mt-3 font-serif text-xl italic text-[var(--color-emerald-deep)]">
              {scent}
            </p>
          </div>

          <div className="mt-9">
            <p className="text-[0.62rem] font-medium uppercase tracking-[0.24em] text-[var(--color-amber)]">
              Description
            </p>
            <p className="mt-3 text-[0.98rem] leading-[1.85] text-[var(--color-charcoal)]/85">
              {description}
            </p>
          </div>

          <div className="mt-10 flex items-baseline justify-between border-t border-[var(--color-rule)] pt-8">
            <span className="font-serif text-3xl text-[var(--color-emerald-deep)]">
              {priceLabel}
            </span>
            <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[var(--color-stone)]">
              Hand-poured · small batch · vegan wax
            </span>
          </div>

          {product.priceCents != null ? (
            <div className="mt-10">
              <div className="flex items-center gap-4">
                <span className="text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
                  Quantity
                </span>
                <div className="flex items-center border border-[var(--color-rule)]">
                  <button
                    type="button"
                    onClick={() => setQuantityState((q) => Math.max(1, q - 1))}
                    className="flex h-10 w-10 items-center justify-center text-[var(--color-emerald-deep)] transition-colors hover:bg-[var(--color-cream)]"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="w-12 text-center font-serif text-lg text-[var(--color-emerald-deep)]">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setQuantityState((q) => Math.min(99, q + 1))
                    }
                    className="flex h-10 w-10 items-center justify-center text-[var(--color-emerald-deep)] transition-colors hover:bg-[var(--color-cream)]"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-4 sm:flex-row">
                <Button type="button" onClick={handleAddToCart}>
                  Add to Cart
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBuyNow}
                  disabled={checkoutState.status === "pending"}
                >
                  {checkoutState.status === "pending"
                    ? "Preparing Checkout…"
                    : "Buy Now"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <ButtonLink to="/create">Enquire About This Piece</ButtonLink>
              <ButtonLink to="/collection" variant="ghost">
                Back to Collection
              </ButtonLink>
            </div>
          )}

          {checkoutState.status === "error" ? (
            <p className="mt-5 text-sm italic text-[var(--color-oxblood)]">
              {checkoutState.message}
            </p>
          ) : null}

          {product.priceCents != null ? (
            <p className="mt-5 text-[0.65rem] uppercase tracking-[0.2em] text-[var(--color-stone)]">
              Secure checkout via Stripe
            </p>
          ) : null}
        </div>
      </section>

      <section className="mt-24">
        <div className="mb-10 flex items-baseline justify-between">
          <div>
            <span className="eyebrow">You might also like</span>
            <h2 className="mt-3 font-serif text-3xl text-[var(--color-emerald-deep)] md:text-4xl">
              From the atelier
            </h2>
          </div>
          <Link
            to="/collection"
            className="text-[0.7rem] uppercase tracking-[0.18em] text-[var(--color-stone)] hover:text-[var(--color-emerald)]"
          >
            all pieces →
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {related.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              className="group block overflow-hidden border border-[var(--color-rule)]/60 bg-[var(--color-warm-white)] transition-all duration-500 hover:shadow-[0_30px_60px_-40px_rgba(15,71,49,0.45)]"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                />
              </div>
              <div className="px-6 py-5">
                <h3 className="font-serif text-xl text-[var(--color-emerald-deep)]">
                  {p.name}
                </h3>
                <p className="mt-1 font-serif text-xs italic text-[var(--color-stone)]">
                  {p.scent}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
